/**
 * Admin Channel Management - with role permissions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Lock, Globe, Users, AlertTriangle, ShieldCheck } from 'lucide-react';
import axios from 'axios';

function Modal({ title, onClose, children, maxWidth = 'max-w-md' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(4px)' }}>
      <div className={`w-full ${maxWidth} rounded-2xl p-6 max-h-[90vh] overflow-y-auto`}
        style={{ background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(0,122,255,0.20)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-n-800 text-base">{title}</h3>
          <button onClick={onClose} className="text-n-500 hover:text-n-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">{label}</label>
      <input
        className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none transition-all"
        style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
        {...props}
      />
    </div>
  );
}

export default function ChannelManagement() {
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', isPublic: true, password: '', maxUsers: 50 });
  const [permForm, setPermForm] = useState({ requiresRole: false, permissions: [] });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [chRes, rolesRes] = await Promise.all([
        axios.get('/api/admin/channels'),
        axios.get('/api/admin/roles'),
      ]);
      setChannels(chRes.data);
      setRoles(rolesRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', description: '', isPublic: true, password: '', maxUsers: 50 });
    setErr('');
    setModal('create');
  };

  const openEdit = (ch) => {
    setSelectedChannel(ch);
    setForm({
      name: ch.name,
      description: ch.description || '',
      isPublic: ch.isPublic,
      password: '',
      maxUsers: ch.maxUsers,
    });
    setErr('');
    setModal('edit');
  };

  const openPermissions = async (ch) => {
    setSelectedChannel(ch);
    try {
      const res = await axios.get(`/api/admin/channels/${ch.id}/permissions`);
      const perms = res.data.map(p => ({
        roleId: p.roleId,
        canListen: p.canListen,
        canTransmit: p.canTransmit,
      }));
      setPermForm({ requiresRole: ch.requiresRole || false, permissions: perms });
    } catch (e) {
      setPermForm({ requiresRole: ch.requiresRole || false, permissions: [] });
    }
    setErr('');
    setModal('permissions');
  };

  const openDelete = (ch) => {
    setSelectedChannel(ch);
    setErr('');
    setModal('delete');
  };

  const closeModal = () => { setModal(null); setSelectedChannel(null); setErr(''); };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await axios.post('/api/channels', {
        name: form.name.trim(),
        description: form.description,
        isPublic: form.isPublic,
        maxUsers: parseInt(form.maxUsers) || 50,
        password: !form.isPublic ? form.password : undefined,
      });
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await axios.put(`/api/channels/${selectedChannel.id}`, {
        name: form.name.trim(),
        description: form.description,
        isPublic: form.isPublic,
        maxUsers: parseInt(form.maxUsers) || 50,
        password: (!form.isPublic && form.password) ? form.password : undefined,
      });
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const handleSavePermissions = async () => {
    setSaving(true); setErr('');
    try {
      await axios.put(`/api/admin/channels/${selectedChannel.id}/permissions`, permForm);
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error al guardar permisos'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true); setErr('');
    try {
      await axios.delete(`/api/channels/${selectedChannel.id}`);
      closeModal(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Error'); }
    finally { setSaving(false); }
  };

  const toggleRolePerm = (roleId, field) => {
    setPermForm(f => {
      const existing = f.permissions.find(p => p.roleId === roleId);
      if (existing) {
        return {
          ...f,
          permissions: f.permissions.map(p =>
            p.roleId === roleId ? { ...p, [field]: !p[field] } : p
          ),
        };
      } else {
        const newPerm = { roleId, canListen: true, canTransmit: true };
        newPerm[field] = true;
        return { ...f, permissions: [...f.permissions, newPerm] };
      }
    });
  };

  const getRolePerm = (roleId, field) => {
    const p = permForm.permissions.find(p => p.roleId === roleId);
    return p ? p[field] : false;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX') : '—';

  const ChannelForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <InputField label="Nombre" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required maxLength={100} />
      <div>
        <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Descripción</label>
        <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
          rows={2} className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none resize-none"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }} />
      </div>
      <InputField label="Máx. usuarios" type="number" min="1" max="500" value={form.maxUsers}
        onChange={e => setForm(f => ({...f, maxUsers: e.target.value}))} />
      <div>
        <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-2">Tipo</label>
        <div className="flex gap-3">
          <button type="button" onClick={() => setForm(f => ({...f, isPublic: true}))}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${form.isPublic ? 'text-n-800' : 'text-n-600'}`}
            style={{ background: form.isPublic ? 'linear-gradient(135deg,#007AFF,#5856D6)' : 'rgba(255,255,255,0.05)' }}>
            <Globe size={13} /> Público
          </button>
          <button type="button" onClick={() => setForm(f => ({...f, isPublic: false}))}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${!form.isPublic ? 'text-n-800' : 'text-n-600'}`}
            style={{ background: !form.isPublic ? 'linear-gradient(135deg,#FF9500,#FF9A3C)' : 'rgba(255,255,255,0.05)' }}>
            <Lock size={13} /> Privado
          </button>
        </div>
      </div>
      {!form.isPublic && (
        <InputField label="Contraseña del canal" type="password" value={form.password}
          onChange={e => setForm(f => ({...f, password: e.target.value}))}
          placeholder={selectedChannel?.hasPassword ? 'Dejar vacío para mantener' : 'Contraseña requerida'} />
      )}
      {err && <p className="text-sm text-s-red">{err}</p>}
      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={saving}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
          {saving ? '...' : submitLabel}
        </button>
        <button type="button" onClick={closeModal}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
          style={{ background: 'rgba(255,255,255,0.05)' }}>
          Cancelar
        </button>
      </div>
    </form>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-n-800">Canales</h1>
          <p className="text-sm text-n-500 mt-0.5">{channels.length} canal(es)</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-n-800 transition-all"
          style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
          <Plus size={14} /> Nuevo Canal
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map(ch => (
            <div key={ch.id} className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.78)', border: `1px solid ${ch.requiresRole ? 'rgba(199,125,255,0.2)' : ch.isPublic ? 'rgba(0,0,0,0.06)' : 'rgba(255,217,61,0.15)'}` }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: ch.requiresRole ? 'rgba(199,125,255,0.1)' : ch.isPublic ? 'rgba(0,0,0,0.06)' : 'rgba(255,159,10,0.12)' }}>
                    {ch.requiresRole
                      ? <ShieldCheck size={13} style={{ color: '#C77DFF' }} />
                      : ch.isPublic
                      ? <Globe size={13} className="text-s-blue" />
                      : <Lock size={13} style={{ color: '#FF9500' }} />
                    }
                  </div>
                  <div>
                    <div className="font-bold text-n-800 text-sm">{ch.name}</div>
                    <div className="text-xs" style={{ color: ch.requiresRole ? '#C77DFF' : ch.isPublic ? '#007AFF' : '#FF9500' }}>
                      {ch.requiresRole ? '🔒👤 Por rol' : ch.isPublic ? 'Público' : 'Privado'}{ch.hasPassword ? ' · 🔑' : ''}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openPermissions(ch)} title="Permisos por rol"
                    className="p-1.5 rounded-lg text-n-500 hover:text-purple-400 hover:bg-purple-500/10 transition-all">
                    <ShieldCheck size={13} />
                  </button>
                  <button onClick={() => openEdit(ch)}
                    className="p-1.5 rounded-lg text-n-500 hover:text-s-blue hover:bg-s-blue/10 transition-all">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => openDelete(ch)}
                    className="p-1.5 rounded-lg text-n-500 hover:text-s-red hover:bg-s-red/10 transition-all">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {ch.description && <p className="text-xs text-n-500 mb-3 line-clamp-2">{ch.description}</p>}
              <div className="flex items-center justify-between text-xs text-n-400">
                <div className="flex items-center gap-1"><Users size={11} /><span>{ch.onlineCount} / {ch.maxUsers}</span></div>
                <span>{ch.transmissionCount || 0} TX</span>
                <span>{formatDate(ch.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal === 'create' && <Modal title="Crear Canal" onClose={closeModal}><ChannelForm onSubmit={handleCreate} submitLabel="Crear" /></Modal>}
      {modal === 'edit' && selectedChannel && <Modal title={`Editar: ${selectedChannel.name}`} onClose={closeModal}><ChannelForm onSubmit={handleEdit} submitLabel="Guardar" /></Modal>}

      {/* Permissions Modal */}
      {modal === 'permissions' && selectedChannel && (
        <Modal title={`Permisos: ${selectedChannel.name}`} onClose={closeModal} maxWidth="max-w-lg">
          <div className="space-y-4">
            {/* Require role toggle */}
            <div className="flex items-center justify-between px-4 py-3 rounded-xl"
              style={{ background: 'rgba(199,125,255,0.06)', border: '1px solid rgba(199,125,255,0.15)' }}>
              <div>
                <div className="text-sm font-semibold text-n-800">Requiere rol</div>
                <div className="text-xs text-n-500">Solo usuarios con rol permitido pueden acceder</div>
              </div>
              <button onClick={() => setPermForm(f => ({ ...f, requiresRole: !f.requiresRole }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${permForm.requiresRole ? 'bg-purple-500' : 'bg-slate-700'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${permForm.requiresRole ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {roles.length === 0 ? (
              <p className="text-xs text-n-500 text-center py-4">No hay roles configurados. Ve a Roles para crear.</p>
            ) : (
              <div>
                <div className="text-xs font-semibold text-n-600 uppercase tracking-wider mb-2">Acceso por rol</div>
                <div className="space-y-2">
                  {roles.map(role => (
                    <div key={role.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{ background: 'rgba(10,15,30,0.5)', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: role.color }} />
                      <span className="text-sm text-n-800 flex-1">{role.name}</span>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox"
                            checked={getRolePerm(role.id, 'canListen')}
                            onChange={() => toggleRolePerm(role.id, 'canListen')}
                            className="w-4 h-4 rounded accent-brand-500" />
                          <span className="text-xs text-n-600">Escuchar</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox"
                            checked={getRolePerm(role.id, 'canTransmit')}
                            onChange={() => toggleRolePerm(role.id, 'canTransmit')}
                            className="w-4 h-4 rounded accent-brand-500" />
                          <span className="text-xs text-n-600">Transmitir</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={closeModal}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700 hover:border-slate-500 transition-all">
                Cancelar
              </button>
              <button onClick={handleSavePermissions} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#C77DFF,#9B59B6)' }}>
                {saving ? 'Guardando...' : 'Guardar Permisos'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'delete' && selectedChannel && (
        <Modal title="Confirmar Eliminación" onClose={closeModal}>
          <div className="flex items-start gap-3 p-4 rounded-xl mb-5"
            style={{ background: 'rgba(255,55,95,0.10)', border: '1px solid rgba(255,55,95,0.20)' }}>
            <AlertTriangle size={16} className="text-s-red mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-n-700">¿Eliminar el canal <strong className="text-s-red">{selectedChannel.name}</strong>?</p>
              <p className="text-xs text-n-500 mt-1">Se eliminarán todos sus mensajes. Esta acción es irreversible.</p>
            </div>
          </div>
          {err && <p className="text-sm text-s-red mb-3">{err}</p>}
          <div className="flex gap-2">
            <button onClick={handleDelete} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'rgba(255,107,107,0.8)' }}>
              {saving ? '...' : 'Eliminar'}
            </button>
            <button onClick={closeModal}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              Cancelar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
